<template>
  <div>
    <nav class="navbar">
      <div class="navbar-container">
        <a class="navbar-brand" href="#">空気環境測定報告管理システム</a>
        <div class="navbar-menu">
          <router-link class="nav-link" v-if="isLoggedIn" :to="{ name: 'Home'}">ホーム</router-link>
          <!-- <a class="nav-link" v-if="isLoggedIn" href="#">ホーム</a> -->
          <div class="nav-item" v-if="isLoggedIn" @click.stop="toggleMasterDropdown" :class="{'dropdown-open': masterDropdownOpen}">
            <span class="nav-link dropdown-toggle">マスタ管理</span>
            <div class="dropdown-menu" v-if="masterDropdownOpen" v-click-outside="closeMasterDropdown">
              <router-link class="dropdown-item" v-if="isLoggedIn" :to="{ name: 'CustomerList'}">顧客情報</router-link>
              <router-link class="dropdown-item" v-if="isLoggedIn" :to="{ name: 'BuildingTypeList'}">建物種別情報</router-link>
              <router-link class="dropdown-item" v-if="isLoggedIn" :to="{ name: 'BuildingList'}">建物情報</router-link>
              <router-link class="dropdown-item" v-if="isLoggedIn" :to="{ name: 'DeviceList'}">測定機器情報</router-link>
              <router-link class="dropdown-item" v-if="isLoggedIn" :to="{ name: 'MeasureTypeList'}">測定種別情報</router-link>
              <router-link class="dropdown-item" v-if="isLoggedIn" :to="{ name: 'RelativeHumidityMatrix'}">相対湿度マトリクス</router-link>
              <router-link class="dropdown-item" v-if="isLoggedIn" :to="{ name: 'MeasureStdList'}">空気環境測定基準値</router-link>
              <router-link class="dropdown-item" v-if="isLoggedIn" :to="{ name: 'UserList'}">ユーザ情報</router-link>
            </div>
          </div>
          <router-link class="nav-link" v-if="isLoggedIn" :to="{ name: 'MeasurementReportList'}">測定報告一覧</router-link>
          <router-link class="nav-link" v-if="isLoggedIn" :to="{ name: 'CustomerBuildingList'}">建物一覧</router-link>
          <!-- IES4一括取込はシステム管理者専用のため、権限がある場合だけメニューに出す -->
          <router-link class="nav-link" v-if="isLoggedIn && canUseIes4Bulk" :to="{ name: 'MeasurementReportIes4BulkImport'}">IES4一括取込</router-link>
          <div class="nav-item" v-if="isLoggedIn" @click.stop="toggleUserDropdown" :class="{'dropdown-open': userDropdownOpen}">
            <span class="nav-link dropdown-toggle">{{ userName }}</span>
            <div class="dropdown-menu" v-if="userDropdownOpen" v-click-outside="closeUserDropdown">
              <button class="nav-link" v-if="isLoggedIn" data-bs-toggle="modal" data-bs-target="#passwordUpdateHeaderModal" @click="handlePasswordUpdateClick(userId)">パスワード更新</button>
              <button class="nav-link logout-button" v-if="isLoggedIn" @click="handleLogout">ログアウト</button>

            </div>
          </div>
        </div>
        <div class="text-right text-nowrap header-actions">
          <button
            v-if="isLoggedIn && canAccessBillingSystem"
            class="billing-link-button"
            type="button"
            :disabled="isSsoLoading"
            @click="moveToBillingSystem"
          >
            {{ isSsoLoading ? '連携中...' : '請求管理システムへ' }}
          </button>
          <router-link v-if="!isLoggedIn" class="nav-link" :to="{ name: 'Login'}">ログイン</router-link>
        </div>
      </div>
    </nav>
    
    <!-- 削除確認モーダル -->
    <PasswordResetHeaderModal
      :userId="paramsIdToPasswordUpdate"
    />
  </div>
</template>

<script>
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { createSystemHandoff, logout } from '@/api/axios';
import { canUseIes4BulkImport, currentRoleFromStoreOrLocalStorage } from '@/utils/roleAuthorization';
import PasswordResetHeaderModal from '@/components/shared/PasswordResetHeaderModal.vue'; // モーダルコンポーネントのインポート

const NO_ACCESS_ROLE = 3;

export default {
  name: "Header",
  components: {
    PasswordResetHeaderModal // コンポーネントの登録
  },
  setup() {
    const store = useStore();
    const router = useRouter();
    const userId = localStorage.getItem('userId');
    const masterDropdownOpen = ref(false);  // マスタ管理用のドロップダウン状態
    const userDropdownOpen = ref(false);  // ユーザメニュー用のドロップダウン状態
    const isSsoLoading = ref(false);

    // モーダルへ渡すID
    const paramsIdToPasswordUpdate = ref(null);

    // パスワード更新
    const handlePasswordUpdateClick = (userId) => {
      paramsIdToPasswordUpdate.value = userId;
    };

    // ドロップダウンの状態を切り替える関数
    const toggleMasterDropdown = () => {
      masterDropdownOpen.value = !masterDropdownOpen.value;
    };

    const toggleUserDropdown = () => {
      userDropdownOpen.value = !userDropdownOpen.value;
    };

    const closeMasterDropdown = () => {
      if (masterDropdownOpen.value) {
        masterDropdownOpen.value = false;
      }
    };

    const closeUserDropdown = () => {
      if (userDropdownOpen.value) {
        userDropdownOpen.value = false;
      }
    };

    const userName = computed(() => store.state.userName);
    const userRole = computed(() => store.state.userRole);

    // Vuex ストアの状態を算出プロパティとして使用
    const isLoggedIn = computed(() => store.state.isLoggedIn);
    const canAccessBillingSystem = computed(() => {
      if (!isLoggedIn.value) return false;

      // Vue reactivity trigger for login/role updates
      const currentRoleValue = userRole.value;
      const rawRolesBySystem = localStorage.getItem('userRolesBySystem');
      let billingRole = Number.NaN;

      if (rawRolesBySystem) {
        try {
          const parsed = JSON.parse(rawRolesBySystem);
          if (parsed && typeof parsed === 'object') {
            billingRole = Number(parsed.billing);
          }
        } catch (error) {
          console.warn('userRolesBySystem parse error:', error);
        }
      }

      if (!Number.isNaN(billingRole)) {
        return billingRole !== NO_ACCESS_ROLE;
      }

      const currentRole = Number(currentRoleValue ?? localStorage.getItem('userRole'));
      if (Number.isNaN(currentRole)) return true;
      return currentRole !== NO_ACCESS_ROLE;
    });
    // IES4の単票取込は建物一覧の「IES4ファイルから報告書を作成」ボタンから開始するため、グローバルメニューには置かない。
    // 一括取込は建物を跨いで扱うため、システム管理者にだけグローバルメニューから開けるようにする。
    const canUseIes4Bulk = computed(() => (
      isLoggedIn.value && canUseIes4BulkImport(currentRoleFromStoreOrLocalStorage(userRole.value))
    ));

    const resolveBillingSystemBaseUrl = () => {
      const envUrl = process.env.VUE_APP_BILLING_APP_URL;
      if (envUrl) return envUrl.replace(/\/$/, '');

      const host = window.location.hostname;
      if (
        host === 'localhost' ||
        host === '127.0.0.1' ||
        /^192\.168\.\d{1,3}\.\d{1,3}$/.test(host)
      ) {
        return 'http://localhost:8080';
      }

      if (host.includes('stg')) {
        return 'https://stg-work-billing-mng.soei-planning.com';
      }

      return 'https://work-billing-mng.soei-planning.com';
    };

    const moveToBillingSystem = async () => {
      const billingSystemBaseUrl = resolveBillingSystemBaseUrl();
      if (!isLoggedIn.value) {
        window.location.href = `${billingSystemBaseUrl}/login`;
        return;
      }

      if (!canAccessBillingSystem.value) {
        store.commit('setFlashMessage', { message: '請求管理システムへのアクセス権限がありません', type: 'warning' });
        return;
      }

      if (isSsoLoading.value) return;

      try {
        isSsoLoading.value = true;
        const response = await createSystemHandoff('billing');
        const handoffToken = response.data?.handoff_token;

        if (!handoffToken) {
          throw new Error('handoff_token is missing');
        }

        const encodedToken = encodeURIComponent(handoffToken);
        window.location.href = `${billingSystemBaseUrl}/sso-entry?sso_token=${encodedToken}`;
      } catch (error) {
        console.error('システム連携エラー:', error);
        store.commit('setFlashMessage', { message: '請求管理システムへの連携に失敗しました', type: 'danger' });
      } finally {
        isSsoLoading.value = false;
      }
    };

    const handleLogout = async () => {
      try {
        await logout();
        store.commit('setLoggedIn', false); // ログアウト時に状態を更新
        store.commit('setUserName', null); // ログアウト時に状態を更新
        store.commit('setUserRole', null); // ログアウト時に状態を更新
        store.commit('setFlashMessage', {message: 'ログアウト成功！', type: 'success'});
        router.push('/login');
      } catch (error) {
        store.commit('setFlashMessage', {message: 'ログアウト失敗！', type: 'danger'});
        console.error('更新エラー:', error);
      }
    };

    return {
      userId,
      userName,
      isLoggedIn,
      isSsoLoading,
      canAccessBillingSystem,
      canUseIes4Bulk,
      moveToBillingSystem,
      paramsIdToPasswordUpdate,
      handlePasswordUpdateClick,
      handleLogout,
      masterDropdownOpen,
      toggleMasterDropdown,
      userDropdownOpen,
      toggleUserDropdown,
      closeMasterDropdown,
      closeUserDropdown,
    };
  }
};
</script>

<style scoped>
.navbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #2c3e50;
  padding: 0 20px;
  position: fixed; /* 画面上部に固定 */
  top: 0; /* トップエッジから0pxの位置に */
  left: 0; /* 左エッジから0pxの位置に */
  width: 100%; /* 幅を100%に設定 */
  z-index: 1000; /* 他の要素より前面に表示 */
}

.navbar-container {
  display: flex;
  /* justify-content: space-between; */
  align-items: center;
  width: 100%;
}

.nav-link, .navbar-brand {
  color: #fff;
  text-decoration: none;
  padding: 10px 15px;
  cursor: pointer;
}

.nav-link:hover, .navbar-brand:hover {
  color: #f39c12;
}

.navbar-menu {
  display: flex;
  justify-content: space-around;
  align-items: center;
  width: 100%;
}

.nav-item{
  position: relative;
}

.dropdown-menu {
  display: none;
  position: absolute;
  background-color: #34495e;
  box-shadow: 0 8px 16px 0 rgba(0,0,0,0.2);
  z-index: 1;
  top: 100%;
  left: 0;
}

.dropdown-item {
  color: #fff;
  padding: 10px 15px;
  text-decoration: none;
  display: block;
}

.dropdown-item:hover {
  background-color: #f39c12;
}

.dropdown-open .dropdown-menu {
  display: block;
}

.logout-button {
  background: none;
  border: none;
  cursor: pointer;
  color: #fff;
  padding: 10px 15px;
}

.logout-button:hover {
  color: #f39c12;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.billing-link-button {
  background: #f39c12;
  border: none;
  color: #fff;
  border-radius: 4px;
  padding: 6px 12px;
  font-size: 13px;
}

.billing-link-button:hover {
  background: #ffaf35;
}

.billing-link-button:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.modal-backdrop {
 z-index: -1 !important;
}

@media (max-width: 768px) {
  .navbar-menu {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }

  .dropdown-menu {
    position: static;
  }
}
</style>
